import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, FileText, Gauge, Star, MapPin, Rocket, BikeIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaClock, FaChartLine, FaPercentage, FaWallet, FaBalanceScale, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { BiBike } from "react-icons/bi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TriumphTwoWheelerLoan = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "What is the typical interest rate for Triumph bike loans?",
      answer: "Interest rates usually range from 7.5% to 12.5% per annum.",
    },
    {
      question: "Can I get a loan for a used Triumph motorcycle?",
      answer: "Yes, many lenders finance certified pre-owned Triumph models.",
    },
    {
      question: "Does Triumph offer its own financing?",
      answer: "Yes, through tie-ups with partner banks and finance companies.",
    },
    {
      question: "What is the minimum down payment required?",
      answer: "Usually between 10% and 25% of the on-road price.",
    },
    {
      question: "What credit score is preferred for approval?",
      answer: "A score of 700 or above improves your chances.",
    },
    {
      question: "Is 100% financing available?",
      answer: "Some lenders offer zero down payment schemes to eligible buyers.",
    },
    {
      question: "How long does loan approval take?",
      answer: "Generally between 24 to 48 hours.",
    },
    {
      question: "Can I prepay my Triumph loan without penalty?",
      answer: "Many lenders allow prepayment after a lock-in period.",
    },
    {
      question: "Can I include insurance in my loan?",
      answer: "Yes, most lenders allow you to include insurance costs in the loan amount.",
    },
    {
      question: "Are loans available for all Triumph models?",
      answer: "Yes, including roadsters, adventure bikes, and cruisers.",
    },
    {
      question: "Can students apply for a Triumph loan?",
      answer: "Yes, but usually with a guarantor.",
    },
    {
      question: "Are there festive or seasonal loan offers?",
      answer: "Yes, banks and dealerships often provide special promotions.",
    },
    {
      question: "Can NRIs apply for a Triumph loan?",
      answer: "Yes, but an Indian co-applicant may be required.",
    },
    {
      question: "What is the maximum loan tenure offered?",
      answer: "Up to 7 years depending on the lender.",
    },
    {
      question: "Can I apply for the loan online?",
      answer: "Yes, via bank websites or TheFinMaster.",
    },
    {
      question: "Is income proof mandatory?",
      answer: "Yes, such as salary slips, ITR, or business documents.",
    },
    {
      question: "Can I transfer my Triumph loan to another bank?",
      answer: "Yes, through a balance transfer option.",
    },
    {
      question: "Are accessories for Triumph bikes financeable?",
      answer: "Yes, if they are part of the purchase invoice.",
    },
    {
      question: "Can I get financing for custom upgrades?",
      answer: "Yes, if included in the official invoice.",
    },
    {
      question: "Do women buyers get special loan benefits?",
      answer: "Some lenders offer reduced interest rates for women buyers.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows EMI Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Loans' },
  ];

  const benefits = [
    {
      title: "Triumph Street Twin",
      desc: "A classic modern roadster ideal for city rides and occasional highway cruising.",
      icon: <Gauge className="w-7 h-7 text-gray-500" />,
      gradient: "from-gray-50 to-gray-100",
    },
    {
      title: "Triumph Bonneville T120",
      desc: "A retro-styled icon with strong performance and comfort.",
      icon: <Star className="w-7 h-7 text-blue-500" />,
      gradient: "from-blue-50 to-blue-100",
    },
    {
      title: "Triumph Tiger 900",
      desc: "A versatile adventure bike built for touring and off-road challenges.",
      icon: <MapPin className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "Triumph Rocket 3",
      desc: "A muscular powerhouse with the world’s largest production motorcycle engine.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
  ];

  const highlights = [
    { icon: <FaRupeeSign className="text-3xl text-blue-700 mb-4" />, feature: "Loan Amount", details: "₹2 lakh to ₹35 lakh" },
    { icon: <FaClock className="text-3xl text-blue-700 mb-4" />, feature: "Loan Tenure", details: "6 months to 7 years" },
    { icon: <FaChartLine className="text-3xl text-blue-700 mb-4" />, feature: "Interest Rate Range", details: "7.5% to 12.5% per annum" },
    { icon: <FaPercentage className="text-3xl text-blue-700 mb-4" />, feature: "Processing Fees", details: "1% to 2.5% of loan amount" },
    { icon: <FaWallet className="text-3xl text-blue-700 mb-4" />, feature: "Minimum Down Payment", details: "10% to 25% of on-road price" },
    { icon: <FaBalanceScale className="text-3xl text-blue-700 mb-4" />, feature: "Loan to Value (LTV)", details: "Up to 90%" },
    { icon: <FaCheckCircle className="text-3xl text-blue-700 mb-4" />, feature: "Approval Timeline", details: "24 to 48 hours after application" },
    { icon: <FaExchangeAlt className="text-3xl text-blue-700 mb-4" />, feature: "EMI Options", details: "Standard EMI, Balloon Payment, Flexible Repayment" },
  ];

  return (
    <>
      <Helmet>
        <title>Triumph Two-Wheeler Loan in India – Compare Rates & Apply Online</title>
        <meta
          name="description"
          content="Explore Triumph two-wheeler loan options in India. Compare interest rates, EMI, and apply online for models like Street Twin, Bonneville T120, Tiger 900, and Rocket 3."
        />
        <meta
          name="keywords"
          content="triumph loan, triumph street twin loan, triumph bonneville t120 loan, triumph tiger 900 loan, triumph rocket 3 loan, two-wheeler loan emi calculator"
        />
         <link rel="canonical" href="https://thefinmaster.com/triump-two-wheeler-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/triumph-two-wheeler-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Triumph Two-Wheeler Loan in India" />
        <meta property="og:description" content="Compare and apply for Triumph two-wheeler loans online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/triumph-two-wheeler-loan" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Triumph Two-Wheeler Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Triumph Two-Wheeler Loan </span>  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Triumph is celebrated for its premium craftsmanship, strong performance, and timeless British design. Whether you are planning to buy a classic Bonneville or an adventure-ready Tiger, the right bike loan can make ownership more convenient and affordable. This guide walks you through everything you need to know about getting a bike loan for a Triumph, from interest rates and eligibility to documentation, repayment tips, and FAQs.                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/triumploan.jpeg"
                alt="Triumph Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            Popular Triumph Models Eligible for Loans
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Most leading banks, NBFCs, and Triumph dealerships in India offer financing for both brand-new and certified pre-owned motorcycles.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="w-full bg-gradient-to-b from-white via-blue-50 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center mb-4">
             Triumph Two-Wheeler Loan Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-4">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden p-6 rounded-2xl shadow-xl bg-white/70 backdrop-blur-xl border border-gray-200 transition-transform transform hover:scale-105"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-300 via-pink-300 to-yellow-300 rounded-full opacity-30 blur-3xl group-hover:opacity-50 transition-all"></div>
                <div className="text-3xl text-blue-700 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.feature}</h3>
                <p className="text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Banks Offering Triumph Two-Wheeler Loans
        </h2>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { lender: "HDFC Bank", rate: "7.5%", tenure: "7 years", features: "High loan amounts, attractive repayment plans" },
              { lender: "ICICI Bank", rate: "8%", tenure: "6 years", features: "Quick approval and online processing" },
              { lender: "State Bank of India", rate: "8.25%", tenure: "5 years", features: "Low processing fees" },
              { lender: "Axis Bank", rate: "8.5%", tenure: "6 years", features: "Flexible prepayment terms" },
              { lender: "Triumph Finance Partners", rate: "Custom", tenure: "5–7 years", features: "Exclusive dealership offers and benefits" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.lender}</h3>
                <div className="text-gray-600 mb-1"><strong>Starting Interest Rate:</strong> {item.rate}</div>
                <div className="text-gray-600 mb-1"><strong>Max Tenure:</strong> {item.tenure}</div>
                <div className="text-gray-600"><strong>Key Features:</strong> {item.features}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Lender</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Interest Rate</th>
                  <th className="p-3 border border-gray-300 text-left">Max Tenure</th>
                  <th className="p-3 border border-gray-300 text-left">Key Features</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["HDFC Bank", "7.5%", "7 years", "High loan amounts, attractive repayment plans"],
                  ["ICICI Bank", "8%", "6 years", "Quick approval and online processing"],
                  ["State Bank of India", "8.25%", "5 years", "Low processing fees"],
                  ["Axis Bank", "8.5%", "6 years", "Flexible prepayment terms"],
                  ["Triumph Finance Partners", "Custom", "5–7 years", "Exclusive dealership offers and benefits"],
                ].map(([lender, rate, tenure, features], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{lender}</td>
                    <td className="p-3 border border-gray-300">{rate}</td>
                    <td className="p-3 border border-gray-300">{tenure}</td>
                    <td className="p-3 border border-gray-300">{features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Two-Wheeler Loan EMI <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your EMI — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/two-wheeler-loan-emi-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Sample EMI Calculation for Triumph Motorcycles
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          You can use TheFinMaster’s EMI calculator to adjust the loan amount, tenure, and interest rate to find the right plan for your budget.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Triumph Street Twin", price: "₹8,50,000", loan: "₹7,65,000", emi: "₹15,667" },
              { model: "Triumph Rocket 3", price: "₹23,00,000", loan: "₹20,70,000", emi: "₹42,328" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>On-Road Price:</strong> {item.price}</div>
                <div className="text-gray-600 mb-1"><strong>Loan Amount:</strong> {item.loan}</div>
                <div className="text-gray-600"><strong>EMI (5 Years @8.5%):</strong> {item.emi}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">On-Road Price (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Loan Amount (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">EMI (5 Years @8.5%)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Triumph Street Twin", "₹8,50,000", "₹7,65,000", "₹15,667"],
                  ["Triumph Rocket 3", "₹23,00,000", "₹20,70,000", "₹42,328"],
                ].map(([model, price, loan, emi], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{price}</td>
                    <td className="p-3 border border-gray-300">{loan}</td>
                    <td className="p-3 border border-gray-300">{emi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Triumph Two-Wheeler Loan
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Salaried Applicants:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300 mb-6">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 3 months’ salary slips</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Form 16 or last 2 years’ ITR</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 6 months’ bank statement</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Proof of address</span>
              </div>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Self-Employed Applicants:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business registration certificate or GSTIN</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 2–3 years’ ITR</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 6 months’ bank statement</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Proof of business address</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BikeIcon />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a Triumph is about experiencing unmatched craftsmanship, thrilling rides, and a distinct sense of style. With flexible loan options, quick approvals, and competitive interest rates, making a Triumph yours is more achievable than ever. TheFinMaster helps you compare offers, choose the ideal repayment plan, and apply with ease so you can hit the road with confidence.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default TriumphTwoWheelerLoan;